import { DocumentCategory, EditingCategory } from 'constants/product'
import { DynamicSummary, SummaryTextBlock } from 'components/common/OrderSummaryRows'
import { EditingProductListing, ProductListing } from 'components/pages/PurchaseFlow/common'
import { isEditingCategory, isShootingCategory } from 'utils/validators'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useProductAndInstructionLists, usePurchaseFlowConfig, usePurchaseFlowOrderMeta, usePurchaseFlowProducts } from 'components/pages/PurchaseFlow/_main/contexts'
import { usePurchaseFlowPaymentStatus, usePurchaseFlowPlaceOrder } from './contexts'
import { useSnackbar } from 'components/contexts/SnackbarService.context'

import { EmbedConsentCheckbox } from 'components/common/Embed/EmbedConsent'
import { EmbedOrderConfirmation } from 'components/common/Embed/EmbedOrderConfirmation'
import { EmbedProductRecap } from '../EmbedProductRecap'
import { EnergyProductListing } from 'components/pages/PurchaseFlow/common/EnergyProductListing/EnergyProductListing.component'
import { KeyboardEventKey } from 'constants/misc'
import { Logistics } from '../Logistics'
import { useEmbedConfig } from 'components/pages/PurchaseFlow/_main/contexts/EmbedConfig.context'
import { useEmbedConsent } from 'components/pages/PurchaseFlow/_main/contexts/EmbedConsent.context'
import { PurchaseFlowPayment } from '../PurchaseFlowPayment'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import Stack from '@mui/material/Stack'
import { StepWrapper } from 'components/pages/PurchaseFlow/common/StepWrapper'
import { SystemMessage } from 'components/common/SystemMessage'
import TransitionAppear from 'components/common/TransitionAppear/TransitionAppear'
import { getCityFromGooglePlace } from 'utils/location'
import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { useTranslation } from 'react-i18next'
import { ScreenEntryLayout, UNMEASURED_SCREEN_ENTRY_LAYOUT, measureScreenEntryLayout } from 'utils/analytics/screenEntryLayout'

export const ValidationController: React.FC = () => {
  const { t } = useTranslation(['order', 'category', 'embed', 'product', 'product_kind', 'purchase_flow'])
  const { isEmbed } = useEmbedConfig()
  const { isConsentRequired, isConsentChecked, setIsConsentChecked } = useEmbedConsent()
  const { spawnErrorToast } = useSnackbar()
  const { orderIsBeingPlaced, orderPlacementHasError } = usePurchaseFlowPlaceOrder()
  const { getProductQuantityList, getInstructionQuantityList } = useProductAndInstructionLists()
  const { isStripeProcessing, setIsStripeProcessing } = usePurchaseFlowPaymentStatus()
  const { orderHasBeenPlaced, orderIsComplete } = usePurchaseFlowPlaceOrder()
  const {
    selectedCategory,
    selectedAssignmentPlace,
    selectedCountryCode,
    externalReportingInstruction,
  } = usePurchaseFlowConfig()
  const {
    selectedKinds,
    selectedProductTypes,
    selectedSegment,
    isOnlyMeasurementProduct,
  } = usePurchaseFlowProducts()
  const {
    shootingComments,
    selectedPaymentMethod,
    isStripePaymentMethod,
    externalReportingField,
  } = usePurchaseFlowOrderMeta()

  const productComments = useMemo(() => {
    if (!selectedCategory) return null
    if (isShootingCategory(selectedCategory) && shootingComments) return shootingComments
    return null
  }, [selectedCategory, shootingComments])

  const cleanupTimeoutRef = useRef<number | undefined>(undefined)
  const logRef = useRef(false)

  const orderOrStripeIsInProgress = useMemo(() =>
    isStripePaymentMethod ? (orderIsBeingPlaced || isStripeProcessing) : orderIsBeingPlaced,
    [isStripeProcessing, isStripePaymentMethod, orderIsBeingPlaced]
  )

  // Stop stripe loading when order placement fails
  useEffect(() => {
    if (orderPlacementHasError && isStripeProcessing) setIsStripeProcessing(false)
  }, [isStripeProcessing, orderPlacementHasError, setIsStripeProcessing])

  // Analytics on entering this page, once per mount. The layout props (ISSUE-4023) are measured one frame after the
  // render; leaving the screen before that sends the entry with the layout unmeasured rather than late, against the
  // next screen's DOM. The mount-only effect is declared first so its cleanup runs first on unmount.
  const entersSentRef = useRef(false)
  const mountedRef = useRef(true)
  const entersOrderPropsRef = useRef<object>({})
  const sendEnters = useCallback((layout: ScreenEntryLayout) => {
    if (entersSentRef.current) return
    entersSentRef.current = true
    logAnalyticsEvent(AnalyticsEvent.PURCHASE_FLOW_ENTERS_ORDER_VALIDATION_STEP, { ...layout, ...entersOrderPropsRef.current })
  }, [])
  useEffect(() => () => {
    mountedRef.current = false
    sendEnters(UNMEASURED_SCREEN_ENTRY_LAYOUT)
  }, [sendEnters])
  useEffect(() => {
    if (logRef.current) return
    logRef.current = true
    entersOrderPropsRef.current = {
      category: selectedCategory,
      countryCode: selectedCountryCode,
      productSegment: selectedSegment,
      productKinds: Array.from(selectedKinds),
      productTypes: Array.from(selectedProductTypes),
      products: [...getProductQuantityList(), ...getInstructionQuantityList()],
      city: getCityFromGooglePlace(selectedAssignmentPlace)
    }
    void measureScreenEntryLayout().then((layout) => { if (mountedRef.current) sendEnters(layout) })
  }, [selectedCategory, selectedAssignmentPlace, selectedKinds, selectedCountryCode, selectedProductTypes, selectedSegment, getProductQuantityList, getInstructionQuantityList, sendEnters])

  const disableTab = useCallback((e: KeyboardEvent) => {
    if (e.key === KeyboardEventKey.TAB) {
      e.preventDefault()
      e.stopPropagation()
    }
  }, [])

  // Disable TAB key when placing order to prevent user from focusing and changing stripe input fields
  useEffect(() => {
    if (orderOrStripeIsInProgress) {
      window.addEventListener('keydown', disableTab)
    }

    return () => {
      window.removeEventListener('keydown', disableTab)
    }
  }, [disableTab, orderOrStripeIsInProgress])

  // Clear purchase flow store when leaving the component after placing an order
  useEffect(() => {
    if ((orderHasBeenPlaced || orderPlacementHasError) && cleanupTimeoutRef.current) {
      window.clearTimeout(cleanupTimeoutRef.current)
    }
  }, [orderHasBeenPlaced, orderPlacementHasError])

  // Warn user if they navigate away while an order is being placed.
  // SX-F13 (W2.6): replaced window.alert with spawnErrorToast so the
  // warning uses the platform's design system instead of a native dialog.
  useEffect(() => {
    if (orderIsBeingPlaced) {
      return () => {
        cleanupTimeoutRef.current = window.setTimeout(() => {
          if (orderIsBeingPlaced) {
            spawnErrorToast(t('step_validation.leave_warning'))
          }
        }, 1000)
      }
    }
  }, [t, orderIsBeingPlaced, spawnErrorToast])

  // Warn about order being processed while leaving the browser tab
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (orderIsBeingPlaced) {
        // Cancel the event
        // Calling preventDefault on the event obj is required to trigger the dialog.
        // This way is supported in all major browsers in their newer versions.
        e.preventDefault()

        // In legacy versions or browsers truthy returnValue was required for triggering the popup. Kept for compat. 
        e.returnValue = t('step_validation.leave_warning')
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => {
      window.removeEventListener('beforeunload', handler)
    }
  }, [orderIsBeingPlaced, t])

  // Terminal embed state (ISSUE-2377): the confirmation replaces the recap once the order is complete. Not on placement:
  // unmounting PurchaseFlowPayment then would skip the confirm of a card / SEPA payment and leave the order unpaid.
  if (isEmbed && orderIsComplete) {
    return (
      <TransitionAppear visible>
        <EmbedOrderConfirmation />
      </TransitionAppear>
    )
  }

  return (
    <TransitionAppear visible>
      <StepWrapper title={t('step_validation.title')} subtitle={t('step_validation.text')}>

        <Stack flexDirection="row" justifyContent="space-between" gap={4}>

          {/** All data related to logistics */}
          <Logistics />

          <Stack width="100%" gap={4}>

            {/** All data related to the products */}
            <SectionedBorderBox title={t(`category:${selectedCategory}`)}>

              {/** Embed shows a flat read-only recap instead of the product listing (ISSUE-3099) */}
              {isEmbed
                ? <EmbedProductRecap />
                : selectedCategory && !isEditingCategory(selectedCategory)
                  ? selectedCategory === DocumentCategory.ENERGY_CERTIFICATE
                    ? <EnergyProductListing onlySelected={true} disabledSelection={true} isProductPreview />
                    : <ProductListing onlySelected={true} disabledSelection={true} displayTypeHeading={false} />
                  : <EditingProductListing onlySelected={true} disabledSelection={true} displayTypeHeading={false} />
              }

              <Stack marginTop={2} gap={2}>
                {/** Product comments */}
                {productComments && selectedCategory !== EditingCategory.EDITING &&
                  <DynamicSummary
                    title={t('step_validation.product_comments')}
                    value={
                      <SummaryTextBlock
                        value={productComments}
                      />
                    }
                  />
                }

                {/** External reporting */}
                {!!externalReportingInstruction && externalReportingField &&
                  <DynamicSummary
                    title={t(`product:p_${externalReportingInstruction.id}`)}
                    value={
                      <SummaryTextBlock
                        value={externalReportingField}
                      />
                    }
                  />
                }
              </Stack>

            </SectionedBorderBox>

            {/* All data related to payment method (STRIPE) */}
            {isStripePaymentMethod &&
              <SectionedBorderBox title={t(`product_kind:${selectedPaymentMethod}`)} flex={1} height="100%">
                <PurchaseFlowPayment disabled={orderOrStripeIsInProgress} />
              </SectionedBorderBox>
            }

            {/** GDPR/T&C consent checkbox, embed only, while consent is missing (ISSUE-2017). Below the
             * summary so it sits in the reading path right before the place-order CTA (ISSUE-3081). */}
            {isEmbed && isConsentRequired &&
              <EmbedConsentCheckbox checked={isConsentChecked} onChange={setIsConsentChecked} />
            }

            {/** What happens after ordering, embed only (ISSUE-3099). Stays visible once consent is given.
              * Measurement-only orders send a technician, not a photographer, hence the key branch. */}
            {isEmbed &&
              <SystemMessage
                variant="system"
                title={t('embed:important_note.title')}
                message={t(isOnlyMeasurementProduct ? 'embed:important_note.message_measurement' : 'embed:important_note.message')}
              />
            }

          </Stack>

        </Stack>

      </StepWrapper>
    </TransitionAppear>
  )
}
